import { useQuery, useQueryClient } from '@tanstack/react-query';

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Box, Fade, LinearProgress, Stack, Typography } from '@mui/material';

import { useApiClient } from '@@common';

import { ContextClient, SubDomainContextsModel } from '../../api/api';
import Spinner from '../../common/components/Spinner';
import { useDesignerScreenContext } from '../../components/Chat/DesignerScreenContext';
import { PresenceIndicator } from '../../components/Presence/PresenceIndicator';
import { usePresence } from '../../components/Presence/usePresence';
import { useCodeBaseInfo } from '../CodeGen/Hooks/useCodeBaseInfo';
import { useCodeGenController } from '../CodeGen/Hooks/useCodeGenController';
import ContextLayerTabBar, { TabValue } from './Components/ContextLayerTabBar';
import { isApplicationArtifact, parseContextArtifactId } from './ContextArtifactSelection';
import { contextDesignerQueryKeys } from './ContextDesignerQueryKeys';
import ContextDomainView from './ContextDomainView';
import ContextServiceConnectionsView from './ContextServiceConnectionsView';
import SchemasProvider from './Contexts/ContextDomainMetadata';
import DomainReadModelView from './DomainReadModelView';
import DomainScopesView from './DomainScopesView';
import { useContextDesignerSnapshot } from './Hooks/useContextDesignerSnapshot';

type ContextViewUrlParams = {
	id: string;
};

export default function ContextView() {
	const { id: contextId } = useParams<ContextViewUrlParams>();
	const client = useApiClient(ContextClient);
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const [name, setName] = useState<string>('Loading...');
	const [domainRefetchKey, setDomainRefetchKey] = useState(0);
	const [applicationRefetchKey, setApplicationRefetchKey] = useState(0);
	const [contextRefreshKey, setContextRefreshKey] = useState(0);
	const [activeDeploymentLayer, setActiveDeploymentLayer] = useState<'domain' | 'application'>('domain');
	const [isRefreshing, setIsRefreshing] = useState(false);

	const requestedTab = searchParams.get('tab');
	const activeTab: TabValue =
		requestedTab === 'service' || requestedTab === 'event-handlers' || requestedTab === 'integration-events'
			? 'domain'
			: (requestedTab as TabValue) || 'domain';
	const selectedArtifactId = searchParams.get('sid') ?? '';

	const { data: designerSnapshot, isLoading, refetch: refetchDesignerSnapshot } = useContextDesignerSnapshot(contextId ?? '', true);
	const context = designerSnapshot?.context;
	const screenSelection = activeTab === 'domain' ? parseContextArtifactId(selectedArtifactId) : null;
	const viewers = usePresence(
		'context',
		context ? contextId : undefined,
		'definition',
		screenSelection ? { itemType: screenSelection.kind, itemId: screenSelection.id } : { itemType: 'tab', itemId: activeTab },
	);
	useDesignerScreenContext({
		ScreenArea: 'Service',
		ScreenName: context?.contextName,
		ContextId: contextId,
		ScreenSelectionType: screenSelection?.kind ?? activeTab,
		OperationId: screenSelection?.kind === 'operation' ? screenSelection.id : undefined,
		EntityId: screenSelection?.kind === 'entity' ? screenSelection.id : undefined,
		EnumId: screenSelection?.kind === 'enum' ? screenSelection.id : undefined,
		EventHandlerId: screenSelection?.kind === 'event-handler' ? screenSelection.id : undefined,
		IntegrationEventId: screenSelection?.kind === 'integration-event' ? screenSelection.id : undefined,
		CodeFileId:
			screenSelection && ['domain-file', 'application-file', 'domain-test-file', 'test-file'].includes(screenSelection.kind) ? screenSelection.id : undefined,
	});

	const { data: subDomainsData } = useQuery<SubDomainContextsModel[]>({
		queryKey: contextDesignerQueryKeys.subDomains(),
		queryFn: () => client.getSubDomains(),
		staleTime: Infinity,
	});

	const subDomainName = subDomainsData?.find((s) => s.subDomain.id === context?.subDomainId)?.subDomain.name;

	const { data: domainCodebaseData } = useCodeBaseInfo(contextId ?? '', 'domain');
	const { data: applicationCodebaseData } = useCodeBaseInfo(contextId ?? '', 'application');

	const { packageDomainLogic, deployDomainLogic, packageApplicationLogic, deployApplicationLogic, trackCodeOperation } = useCodeGenController(contextId ?? '');

	const handleRefresh = useCallback(async () => {
		if (!contextId) {
			return;
		}

		setIsRefreshing(true);
		try {
			// refetch() bypasses staleTime. Fetch the aggregate snapshot first, then reload
			// independently queried panels so a cached child query cannot win a refresh race.
			await refetchDesignerSnapshot();
			await Promise.all([
				queryClient.refetchQueries({
					type: 'all',
					predicate: (query) =>
						!contextDesignerQueryKeys.isDesignerSnapshot(query.queryKey) && contextDesignerQueryKeys.isForContext(query.queryKey, contextId),
				}),
				queryClient.refetchQueries({ queryKey: contextDesignerQueryKeys.subDomains(), type: 'all' }),
				queryClient.refetchQueries({ queryKey: contextDesignerQueryKeys.availableServiceConnections(), type: 'all' }),
				queryClient.refetchQueries({ queryKey: contextDesignerQueryKeys.serviceConnectionMetadata(), type: 'all' }),
			]);
			setContextRefreshKey((key) => key + 1);
		} finally {
			setIsRefreshing(false);
		}
	}, [contextId, queryClient, refetchDesignerSnapshot]);

	useEffect(() => {
		if (context && context.contextName) {
			setName(context.contextName);
		}
	}, [context]);

	useEffect(() => {
		const artifact = parseContextArtifactId(selectedArtifactId);
		if (artifact) {
			setActiveDeploymentLayer(isApplicationArtifact(artifact) ? 'application' : 'domain');
		} else if (activeTab !== 'domain') {
			setActiveDeploymentLayer('application');
		}
	}, [activeTab, selectedArtifactId]);

	useEffect(() => {
		if (!contextId || !designerSnapshot) return;

		queryClient.setQueryData(contextDesignerQueryKeys.context(contextId), designerSnapshot.context);
		queryClient.setQueryData(contextDesignerQueryKeys.artifactLayout(contextId), designerSnapshot.artifactLayout);
		queryClient.setQueryData(contextDesignerQueryKeys.domainModel(contextId), designerSnapshot.domainModel);
		queryClient.setQueryData(contextDesignerQueryKeys.operations(contextId), designerSnapshot.operations);

		if (designerSnapshot.code?.domainCodeBase) {
			queryClient.setQueryData(['codeBase', contextId, 'domain'], designerSnapshot.code.domainCodeBase);
		}
		if (designerSnapshot.code?.applicationCodeBase) {
			queryClient.setQueryData(['codeBase', contextId, 'application'], designerSnapshot.code.applicationCodeBase);
		}
	}, [contextId, designerSnapshot, queryClient]);

	if (isLoading) {
		return <Spinner />;
	}

	if (!context) {
		return (
			<Stack direction="column" sx={{ flexGrow: 1 }}>
				<Typography color="error">Context not found</Typography>
			</Stack>
		);
	}

	return (
		<>
			{contextId && (
				<SchemasProvider contextId={contextId}>
					<Stack direction="column" sx={{ flexGrow: 1, minHeight: 0, position: 'relative' }}>
						{/* Unified tab bar: breadcrumbs + onion circle + tabs + deployment status */}
						<Box sx={(theme) => ({ px: `${theme.layout.edgeGap}px`, pt: `${theme.layout.edgeGap}px`, border: 0, boxShadow: 'none' })}>
							<ContextLayerTabBar
								activeTab={activeTab}
								onTabChange={(tab) => {
									const nextParams = new URLSearchParams(searchParams);
									nextParams.set('tab', tab);
									navigate(`/service/context/${contextId}?${nextParams.toString()}`, { replace: true });
								}}
								contextId={contextId}
								breadcrumbs={[
									{ label: 'Services', onClick: () => navigate('/service') },
									{ label: subDomainName ?? 'Contexts', onClick: () => navigate(`/service/${context.subDomainId}`) },
									{ label: name, adornment: <PresenceIndicator viewers={viewers} /> },
								]}
								domainCodebaseState={domainCodebaseData?.state}
								applicationCodebaseState={applicationCodebaseData?.state}
								onPackageDomain={packageDomainLogic}
								onDeployDomain={deployDomainLogic}
								onPackageApplication={packageApplicationLogic}
								onDeployApplication={deployApplicationLogic}
								onTrackCodeOperation={trackCodeOperation}
								onDomainAdvanced={() => setDomainRefetchKey((k) => k + 1)}
								onApplicationAdvanced={() => setApplicationRefetchKey((k) => k + 1)}
								activeDeploymentLayer={activeDeploymentLayer}
								onActiveDeploymentLayerChange={setActiveDeploymentLayer}
								onRefresh={handleRefresh}
								refreshing={isRefreshing}
							/>
						</Box>
						<Fade in={isRefreshing} timeout={180} unmountOnExit>
							<LinearProgress
								aria-label="Refreshing context artifacts"
								sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 5, height: 2, pointerEvents: 'none' }}
							/>
						</Fade>

						{/* All tab views stay mounted — visibility toggled via display to avoid remount */}
						<Box sx={{ display: activeTab === 'domain' ? 'flex' : 'none', flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
							{contextId && (
								<ContextDomainView
									contextId={contextId}
									onRefreshContext={handleRefresh}
									refetchTrigger={domainRefetchKey}
									applicationRefetchTrigger={applicationRefetchKey}
									contextRefreshTrigger={contextRefreshKey}
									initialContext={designerSnapshot?.context}
									initialOperationExecutionTypes={designerSnapshot?.operationExecutionTypes}
									initialCodeSnapshot={designerSnapshot?.code}
									domainCodebaseState={domainCodebaseData?.state}
									applicationCodebaseState={applicationCodebaseData?.state}
									activeDeploymentLayer={activeDeploymentLayer}
									onActiveDeploymentLayerChange={setActiveDeploymentLayer}
								/>
							)}
						</Box>
						<Box sx={{ display: activeTab === 'connectors' ? 'flex' : 'none', flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
							{contextId && <ContextServiceConnectionsView contextId={contextId} initialContext={designerSnapshot?.context} />}
						</Box>
						<Box sx={{ display: activeTab === 'read-models' ? 'flex' : 'none', flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
							{contextId && <DomainReadModelView contextId={contextId} />}
						</Box>
						<Box sx={{ display: activeTab === 'authorization' ? 'flex' : 'none', flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
							{contextId && <DomainScopesView contextId={contextId} />}
						</Box>
					</Stack>
				</SchemasProvider>
			)}
		</>
	);
}
